import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetTasksQuery } from "../../services/rtkClient";
import {
    PieChart, Pie, Cell,
    ResponsiveContainer,
    RadialBarChart, RadialBar,
    BarChart, Bar, XAxis, YAxis, Tooltip
} from 'recharts';
import './Summary.css';
import {
    Home,
    PieChart as ChartPie,
    Check,
    Users,
    Settings,
    Search,
    Bell,
    Plus,
    MoreHorizontal,
    Filter,
    Clock,
    Pause,
    Calendar,
    AlertTriangle,
    Circle,
    ChevronLeft
} from 'lucide-react';
import Navbar from '../../components/Navbar';
// --- Icons (Simulated with Unicode/Text) ---
// --- Icons ---
const Icons = {
    Home: () => <Home size={18} />,
    Dashboard: () => <ChartPie size={18} />,
    Tasks: () => <Check size={18} />,
    Team: () => <Users size={18} />,
    Settings: () => <Settings size={18} />,
    Search: () => <Search size={18} />,
    Bell: () => <Bell size={18} />,
    Plus: () => <Plus size={18} />,
    Menu: () => <MoreHorizontal size={18} />,
    Filter: () => <Filter size={18} />,
};

const getCurrentUser = () => {
    try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) return JSON.parse(storedUser);
    } catch (err) { }
    return null;
};

const Summary = () => {
    const location = useLocation();
    const currentUserInfo = getCurrentUser();
    const currentUserName = currentUserInfo ? (currentUserInfo.firstName ? `${currentUserInfo.firstName} ${currentUserInfo.lastName || ''}`.trim() : (currentUserInfo.username || currentUserInfo.email || "Current User")) : "Current User";
    const navigate = useNavigate();
    const projectName = location.state?.projectName || sessionStorage.getItem('projectName') || "";

    const currentProjectId = useMemo(() => {
        if (location.state?.projectId) {
            const id = Number(location.state.projectId);
            sessionStorage.setItem('projectId', String(id));
            return id;
        }
        const stored = sessionStorage.getItem('projectId');
        if (stored) return Number(stored);
        return null; // Avoid default 1
    }, [location.state?.projectId]);

    const { data: serverTasksData, isLoading: tasksLoading } = useGetTasksQuery(currentProjectId, { skip: !currentProjectId });

    const normalizedTasks = useMemo(() => {
        const rawTasks = Array.isArray(serverTasksData) ? serverTasksData : (location.state?.tasks || []);
        let all = [];
        if (Array.isArray(rawTasks)) {
            all = rawTasks.map(t => ({
                ...t,
                id: t.id || t.issueId || `KAN-${t.id || t.issueId}`,
                title: t.summary || t.task || t.title || "Untitled",
                task: t.summary || t.task || t.title || "Untitled"
            }));
        } else {
            Object.entries(rawTasks).forEach(([status, taskList]) => {
                if (Array.isArray(taskList)) {
                    taskList.forEach(t => all.push({ 
                        ...t, 
                        status,
                        id: t.id || t.issueId || `KAN-${t.id || t.issueId}`,
                        title: t.summary || t.task || t.title || "Untitled",
                        task: t.summary || t.task || t.title || "Untitled"
                    }));
                }
            });
        }
        return all.filter(t => !currentProjectId || Number(t.projectId || t.project_id) === currentProjectId);
    }, [serverTasksData, location.state?.tasks, currentProjectId]);

    const { statusData, priorityData, progress, totalTasksCount, doneTasksCount, counts } = useMemo(() => {
        const normalizeStatus = (s) => {
            if (!s) return 'To Do';
            const lowerS = s.toLowerCase().replace(/\s/g, '');
            if (lowerS === 'todo') return 'To Do';
            if (lowerS === 'inprogress') return 'In Progress';
            if (lowerS === 'review' || lowerS === 'inreview') return 'In Review';
            if (lowerS === 'done' || lowerS === 'completed') return 'Completed';
            return s;
        };

        const statusCounts = { 'To Do': 0, 'In Progress': 0, 'In Review': 0, 'Completed': 0 };
        const priorityCounts = { High: 0, Medium: 0, Low: 0, Unassigned: 0 };

        normalizedTasks.forEach(t => {
            const s = normalizeStatus(t.status);
            if (statusCounts[s] !== undefined) statusCounts[s]++;

            const p = t.priority || 'Medium';
            if (priorityCounts[p] !== undefined) priorityCounts[p]++;
            else priorityCounts.Unassigned++;
        });

        const total = normalizedTasks.length || 1;
        const done = statusCounts['Completed'];

        const sData = [
            { name: 'To Do', value: statusCounts['To Do'], color: '#42526E' },
            { name: 'In Progress', value: statusCounts['In Progress'], color: '#0052CC' },
            { name: 'In Review', value: statusCounts['In Review'], color: '#6554C0' },
            { name: 'Completed', value: statusCounts['Completed'], color: '#36B37E' },
        ].filter(item => item.value > 0);

        if (sData.length === 0) {
            sData.push({ name: 'No Tasks', value: 1, color: '#DFE1E6' });
        }

        const pData = [
            { name: 'High', value: priorityCounts.High, color: '#FF5630' },
            { name: 'Medium', value: priorityCounts.Medium, color: '#FFAB00' },
            { name: 'Low', value: priorityCounts.Low, color: '#36B37E' },
            { name: 'Unassigned', value: priorityCounts.Unassigned, color: '#DFE1E6' },
        ].filter(item => item.value > 0 || item.name !== 'Unassigned');

        return {
            statusData: sData,
            priorityData: pData,
            progress: Math.round((done / total) * 100),
            totalTasksCount: normalizedTasks.length,
            doneTasksCount: done,
            counts: statusCounts
        };
    }, [normalizedTasks]);

    const workTypeData = [
        { name: 'Tasks', value: totalTasksCount, color: '#36B37E' },
        { name: 'Stories', value: 0, color: '#0052CC' },
        { name: 'Subtasks', value: 0, color: '#FFAB00' },
        { name: 'Epics', value: 0, color: '#6554C0' },
    ];

    const recentActivity = normalizedTasks.slice(0, 4).map((t, i) => ({
        id: i,
        user: t.assignee || currentUserName,
        action: (t.status === 'Completed' || t.status === 'done') ? 'completed' : 'updated',
        item: t.title || t.task || 'Unnamed Task',
        time: t.date || t.created || 'recently'
    }));

    const epicData = [
        { name: 'completed', uv: progress, fill: '#0052CC' },
        { name: 'total', uv: 100, fill: '#EBECF0' }
    ];

    return (
        <div className="summary-wrapper">
            <Navbar />
            <div className="summary-container">
                {/* Main Content */}
                <div className="summary-main">
                    <div className="summary-content">
                        {/* Toolbar */}
                        <div className="summary-toolbar" style={{ paddingLeft: 0, paddingRight: 0, justifyContent: 'space-between', borderBottom: 'none' }}>
                            <div className="tool-group">
                                <button className="summary-btn" onClick={() => navigate(-1)} title="Back">
                                    <ChevronLeft size={16} /> Back
                                </button>
                                <h2 style={{ marginLeft: '10px', marginRight: '20px', color: '#172B4D' }}>{projectName}</h2>
                                <button className="summary-btn"><Icons.Dashboard /> {totalTasksCount}</button>
                                <button className="summary-btn"><Icons.Dashboard /> View</button>
                                <button className="summary-btn"><Icons.Filter /></button>
                                <button className="summary-btn"><Icons.Menu /></button>
                            </div>

                        </div>

                        <div className="lp-tabs" style={{ marginBottom: '24px', borderBottom: '1px solid #DFE1E6' }}>
                            <div className="lp-tab active">Summary</div>
                            <div className="lp-tab" onClick={() => navigate("/tasks", { state: { projectId: currentProjectId, projectName } })}>Board</div>
                            <div className="lp-tab" onClick={() => navigate("/listpage", { state: { projectId: currentProjectId, projectName } })}>Tasks List</div>
                            <div className="lp-tab" onClick={() => navigate("/timeline", { state: { projectId: currentProjectId, projectName } })}>Timeline</div>
                            <div className="lp-tab" onClick={() => navigate("/calendar", { state: { projectId: currentProjectId, projectName } })}>Calendar</div>
                        </div>

                        <div className="dashboard-grid">

                            {/* Overall Progress Card */}
                            <div className="card area-progress">
                                <div className="card-title">
                                    <span>Overall Progress</span>
                                    <Icons.Settings />
                                </div>
                                <div style={{ height: '140px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {/* Simulated Radial Progress */}
                                    <div style={{
                                        width: '100px', height: '100px', borderRadius: '50%',
                                        border: '10px solid #E3FCEF',
                                        borderTop: `10px solid ${progress > 0 ? '#36B37E' : '#DFE1E6'}`,
                                        borderRight: '10px solid #36B37E',
                                        transform: 'rotate(45deg)'
                                    }}></div>
                                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#172B4D' }}>{progress}%</div>
                                        <div style={{ fontSize: '12px', color: '#36B37E', fontWeight: 'bold' }}>{progress === 100 ? 'Done' : 'On Track'}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Middle Stats Area */}
                            <div className="area-stats">
                                <div className="stat-box">
                                    <div className="stat-icon-box"><Clock size={18} /></div>
                                    <div>
                                        <div style={{ fontWeight: 'bold', fontSize: '18px' }}>{doneTasksCount} updated</div>
                                        <div style={{ fontSize: '12px', color: '#6B778C' }}>Closed, Done Tasks</div>
                                    </div>
                                </div>
                                <div className="stat-box">
                                    <div className="stat-icon-box"><Pause size={18} /></div>
                                    <div>
                                        <div style={{ fontWeight: 'bold', fontSize: '18px' }}>{totalTasksCount} total</div>
                                        <div style={{ fontSize: '12px', color: '#6B778C' }}>Tasks in project</div>
                                    </div>
                                </div>
                                <div className="stat-box">
                                    <div className="stat-icon-box"><Calendar size={18} /></div>
                                    <div>
                                        <div style={{ fontWeight: 'bold', fontSize: '18px' }}>{counts['In Progress']} active</div>
                                        <div style={{ fontSize: '12px', color: '#6B778C' }}>Tasks in progress</div>
                                    </div>
                                </div>

                                {/* Ribbons Row */}
                                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '20px', marginTop: '10px' }}>
                                    <div className="status-ribbon-item bg-green">
                                        <div className="label"><Check size={14} style={{ marginRight: 5 }} /> Done</div>
                                        <div className="count">{doneTasksCount} Tasks</div>
                                    </div>
                                    <div className="status-ribbon-item bg-orange">
                                        <div className="label"><AlertTriangle size={14} style={{ marginRight: 5 }} /> In Progress</div>
                                        <div className="count">{counts['In Progress']} Tasks</div>
                                    </div>
                                    <div className="status-ribbon-item bg-dark">
                                        <div className="label"><Pause size={14} style={{ marginRight: 5 }} /> To Do</div>
                                        <div className="count">{counts['To Do']} Tasks</div>
                                    </div>
                                </div>
                            </div>

                            {/* Status Overview */}
                            <div className="card area-status-overview">
                                <div className="card-title">Status Overview</div>
                                <div style={{ display: 'flex', height: '85%', alignItems: 'center' }}>
                                    <div style={{ flex: 1, height: '100%' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={statusData}
                                                    innerRadius={60}
                                                    outerRadius={80}
                                                    paddingAngle={0}
                                                    dataKey="value"
                                                    stroke="none"
                                                >
                                                    {statusData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div style={{ flex: 1, paddingRight: '20px' }}>
                                        {statusData.map(item => {
                                            const pct = Math.round((item.value / totalTasksCount) * 100) || 0;
                                            return (
                                                <div key={item.name} style={{ marginBottom: '15px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '13px', fontWeight: '500', color: '#172B4D' }}>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color }}></div>
                                                            {item.name}
                                                        </span>
                                                        <span>{pct}%</span>
                                                    </div>
                                                    <div style={{ height: '6px', width: '100%', background: '#EBECF0', borderRadius: '3px' }}>
                                                        <div style={{ height: '100%', width: `${pct}%`, background: item.color, borderRadius: '3px' }}></div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>


                            {/* Priority Breakdown - Custom Bar look with progress bars list */}
                            <div className="card area-priority">
                                <div className="card-title">
                                    <span>Priority Breakdown</span>
                                    <a href="#" style={{ fontSize: '11px', color: '#0052CC', textDecoration: 'none' }}>report with teams</a>
                                </div>
                                <div style={{ fontSize: '11px', color: '#6B778C', marginBottom: '20px' }}>Task counts by priority level</div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {priorityData.map((item, index) => {
                                        const pct = Math.round((item.value / totalTasksCount) * 100) || 0;
                                        return (
                                            <div key={index} style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                                                <div style={{ width: '90px', display: 'flex', alignItems: 'center', gap: '6px', color: '#172B4D' }}>
                                                    <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: item.color }}></div>
                                                    {item.name}
                                                </div>
                                                <div style={{ width: '40px', textAlign: 'right', marginRight: '10px', color: '#172B4D', fontWeight: '500' }}>{item.value}</div>
                                                <div style={{ flex: 1, height: '24px', background: '#F4F5F7', borderRadius: '3px', position: 'relative' }}>
                                                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: item.color, borderRadius: '3px', opacity: 0.8 }}></div>
                                                    <span style={{ position: 'absolute', right: 5, fontSize: 10, lineHeight: '24px', color: '#6B778C' }}>{item.value} {item.value === 1 ? 'Task' : 'Tasks'}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Types of Work */}
                            <div className="card area-work-types">
                                <div className="card-title">Types of work</div>
                                <div style={{ fontSize: '11px', color: '#6B778C', marginBottom: '20px' }}>Distribution of work across items</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {workTypeData.map((item, index) => {
                                        const pct = item.value > 0 ? 100 : 0; // Currently all are "Tasks" if we only have one type
                                        return (
                                            <div key={index}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px', color: '#172B4D', fontWeight: '500' }}>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <div style={{ width: 8, height: 8, background: item.color, borderRadius: 1 }}></div>
                                                        {item.name}
                                                    </span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div style={{ background: '#42526E', color: 'white', fontSize: '11px', padding: '2px 8px', borderRadius: '3px', minWidth: '35px', textAlign: 'center' }}>{item.value}</div>
                                                    <div style={{ flex: 1, height: '8px', background: '#EBECF0', borderRadius: '4px' }}>
                                                        <div style={{ width: `${pct}%`, height: '100%', background: item.color, borderRadius: '4px' }}></div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <a href="#" style={{ fontSize: '12px', color: '#0052CC', marginTop: '10px', display: 'block', textDecoration: 'none' }}>View detailed breakdown</a>
                                </div>
                            </div>

                            {/* Recent Activity */}
                            <div className="card area-activity">
                                <div className="card-title">
                                    <span>Recent activity</span>
                                    <Icons.Menu />
                                </div>
                                {recentActivity.map(act => (
                                    <div key={act.id} className="activity-item">
                                        <div className="activity-avatar" style={{ background: '#DFE1E6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>
                                            IMG
                                        </div>
                                        <div className="activity-content">
                                            <strong>{act.user}</strong> <span style={{ color: '#42526E' }}>{act.action}</span> <a href="#" style={{ color: '#0052CC', textDecoration: 'none', fontWeight: '500' }}>{act.item}</a>
                                            <div style={{ fontSize: '12px', color: '#6B778C', marginTop: '2px' }}>{act.item}</div>
                                        </div>
                                        <div className="activity-time">{act.time}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Epic Progress */}
                            <div className="card area-epic">
                                <div className="card-title">Epic progress</div>
                                <div style={{ height: '180px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <RadialBarChart
                                            innerRadius="70%"
                                            outerRadius="100%"
                                            data={[epicData[0]]}
                                            startAngle={180}
                                            endAngle={0}
                                            cy="70%"
                                        >
                                            <RadialBar
                                                minAngle={15}
                                                background={{ fill: '#EBECF0' }}
                                                clockWise
                                                dataKey="uv"
                                                cornerRadius={10}
                                            />
                                        </RadialBarChart>
                                    </ResponsiveContainer>
                                    <div style={{ position: 'absolute', top: '65%', textAlign: 'center', transform: 'translateY(-50%)' }}>
                                        <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#172B4D' }}>{progress}%</div>
                                        <div style={{ fontSize: '12px', color: '#172B4D', fontWeight: '600', maxWidth: '80px', margin: '0 auto' }}>{doneTasksCount} Issues Completed</div>
                                    </div>
                                </div>
                                <div style={{ marginTop: '0px', fontSize: '13px', color: '#172B4D', textAlign: 'center' }}>
                                    <span style={{ marginRight: '5px' }}><Circle size={10} /></span> Who's an epic?
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Summary;
